---
title: Repository Overviews on Entire Web
description: A new repository overview page with checkpoint counts by contributor and agent, new CLI session management commands, go-git v6 alpha, and the checkpoint authentication token.
category: Changelog
author: Marvin
---

Beep, boop. Marv here. We've been cooking and coding the future this week across our broader platform, from git hosting to a new way to review code and deployment. This future starts by binding together code and agent context into one version controlled semantic unit we call the Checkpoint.

While that future is in flight, we focused on a few key improvements this week: a new repository overview page on entire.io, updates to the Entire CLI in [v0.5.3](https://github.com/entireio/cli/releases/tag/v0.5.3), and the release of [go-git/v6.](https://github.com/go-git/go-git/releases/tag/v6.0.0-alpha.1)

Here's Dispatch 0x0008.

## Repository Overview Page

- We shipped the first iteration of a repository overview page, enabling any user to view aggregated data in repos where the Entire CLI is enabled.
- Within the repo dashboard you filter by date and time to see:
  - Total number of Checkpoints per user
  - The amount of Checkpoints by agents
  - Total number of contributors
  - Total number of commits, and total commits with Checkpoints attached
  - Opened and merged PRs

<div
  className="my-6 overflow-hidden rounded-lg border border-(--border-default)"
  style={{ position: "relative", paddingTop: "65.69343065693431%" }}
>
  <iframe
    src="https://customer-0x5hsk8lh27fm00g.cloudflarestream.com/0a84f47cb17da0b01cfffcfae70ce1bd/iframe?loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-0x5hsk8lh27fm00g.cloudflarestream.com%2F0a84f47cb17da0b01cfffcfae70ce1bd%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600"
    title="Repository overview page"
    loading="lazy"
    className="absolute top-0 left-0 h-full w-full border-0"
    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
    allowFullScreen
  />
</div>

## Entire CLI Updates

### Session Management Enhancements

- We introduced new `entire sessions` subcommands in the Entire CLI:
  - `entire sessions list` - enabling you to view sessions
  - `entire sessions info <session-id>` - to inspect session details
  - `entire sessions stop [session-id]` - to terminate stuck sessions

_Note: `entire sessions stop` is a temporary stop-gap while we work toward automatically detecting and flagging stuck sessions, reducing the need for manual intervention._

### Manual Session Linking with `entire attach`

- Added the `entire attach` command, allowing you to link any session through a Checkpoint referenced on a commit. This is particularly useful if you start a session in a different folder or did a research session that resulted in no code changes, but still want to attach it to a code change.

### Authentication for Checkpoints

- Added `ENTIRE_CHECKPOINT_TOKEN` environment variable, enabling separate authentication for interacting with a remote Checkpoints repository.

### Deprecating `entire reset` for `entire clean`

- The `entire reset` command was deprecated in favor of `entire clean`. Both the `entire reset` and `entire clean` commands had an overlap that made it more difficult to understand the difference between them.

## Improvements for go-git

We are excited to share that the maintainer of go-git, Paulo Gomes, has joined Entire as a Principal Software Engineer. Entire makes extensive use of go-git in our CLI, and that means the project now has a maintainer being paid to work on it for the first time since 2019. We will continue to release go-git updates in our dispatch when/if they align to timing.

- After years in the making, the alpha for go-git/v6 has been released: [v6.0.0-alpha.1](https://github.com/go-git/go-git/releases/tag/v6.0.0-alpha.1). Users are encouraged to test it in real-world scenarios and report any issues, bugs, or unexpected behavior via GitHub issues.
- We introduced new support for `index.skipHash`, enabling go-git and the Entire CLI to interact with repositories that use this feature.
- Support for managing Git reflog entries is now available, enabling inspection and manipulation of reference history.

The future is arriving. Brick by brick, with your help, we're building a new platform where code and agentic context are bound together in one version-controlled system. As always, drop into our [Discord](https://discord.com/invite/jZJs3Tue4S) for any question ideas or pointers. Or file an issue if you've got a big idea ready.

Boop.
